import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { DataTable, type Column } from '../components/data-table';

interface UserRow {
  readonly id: string;
  readonly displayName: string | null;
  readonly primaryLanguage: string;
  readonly currentCountry: string | null;
  readonly homeCountry: string | null;
  readonly knownLanguages: readonly string[];
  readonly interests: readonly string[];
  readonly onboardingCompleted: boolean | null;
  readonly visaType: string | null;
  readonly lifeStage: string | null;
  readonly createdAt: string | null;
  readonly updatedAt: string | null;
}

interface UsersListResponse {
  readonly rows: readonly UserRow[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

const PAGE_SIZE = 50;

const columns: readonly Column<UserRow>[] = [
  {
    key: 'displayName',
    header: 'Name',
    render: (r) => r.displayName ?? <span style={{ color: 'var(--text-muted)' }}>—</span>,
  },
  {
    key: 'primaryLanguage',
    header: 'Language',
    render: (r) => <span className="nw-chip">{r.primaryLanguage}</span>,
    width: '120px',
  },
  {
    key: 'currentCountry',
    header: 'Country',
    render: (r) => r.currentCountry ?? '—',
    width: '100px',
  },
  {
    key: 'homeCountry',
    header: 'Home',
    render: (r) => r.homeCountry ?? '—',
    width: '100px',
  },
  {
    key: 'onboardingCompleted',
    header: 'Onboarded',
    render: (r) =>
      r.onboardingCompleted ? (
        <span className="nw-chip nw-chip--success">Yes</span>
      ) : (
        <span className="nw-chip nw-chip--warning">No</span>
      ),
    width: '120px',
  },
  {
    key: 'visaType',
    header: 'Visa',
    render: (r) =>
      r.visaType ? <span className="nw-chip">{r.visaType}</span> : '—',
    width: '120px',
  },
  {
    key: 'createdAt',
    header: 'Signed up',
    render: (r) =>
      r.createdAt ? (
        <span title={r.createdAt} style={{ color: 'var(--text-secondary)' }}>
          {formatDistanceToNow(new Date(r.createdAt), { addSuffix: true })}
        </span>
      ) : (
        '—'
      ),
    width: '160px',
  },
];

export function UsersList() {
  const [page, setPage] = useState(1);
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('');
  const [q, setQ] = useState('');

  const params = { page, pageSize: PAGE_SIZE, country, language, q };
  const query = useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: async () => {
      const search = new URLSearchParams();
      search.set('page', String(page));
      search.set('pageSize', String(PAGE_SIZE));
      if (country) search.set('country', country);
      if (language) search.set('language', language);
      if (q) search.set('q', q);
      const res = await apiClient.get<UsersListResponse>(
        `/owner/users?${search.toString()}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });

  const data = query.data;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Users</h1>
        <p className="nw-page-header__subtitle">
          Every NewsWave account. Filter by country / language / name and drill in for full profile.
        </p>
      </header>

      <div className="nw-filters">
        <input
          className="nw-filters__input"
          placeholder="Search by display name…"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
        />
        <input
          className="nw-filters__input"
          placeholder="Country (e.g. SE)"
          value={country}
          maxLength={2}
          onChange={(e) => {
            setPage(1);
            setCountry(e.target.value);
          }}
        />
        <input
          className="nw-filters__input"
          placeholder="Language (e.g. en)"
          value={language}
          maxLength={3}
          onChange={(e) => {
            setPage(1);
            setLanguage(e.target.value);
          }}
        />
      </div>

      <DataTable
        columns={columns}
        rows={data?.rows ?? []}
        rowKey={(r) => r.id}
        total={data?.total}
        page={data?.page ?? page}
        pageSize={data?.pageSize ?? PAGE_SIZE}
        onPageChange={setPage}
        loading={query.isLoading || query.isFetching}
        error={query.error ? (query.error as Error).message : null}
        emptyMessage="No users match these filters."
      />
    </>
  );
}
